import React, { useState } from "react";
import CodeBlock from "./CodeBlock.client";
import { ClientOnly } from "remix-utils/client-only";

export default function Config() {
  const [copySuccess, setCopySuccess] = useState(false);

  const copyEmpty = "https://img.icons8.com/?size=100&id=pNYOTp5DinZ3&format=png&color=ffffff";
  const copyFull = "https://img.icons8.com/?size=100&id=AGLXTiVVflkt&format=png&color=ffffff";

  const configCode = `module.exports = ({ env }) => ({
  headlockr: {
    enabled: true,
    config: {
      rateLimit: {
        enabled: true,
        interval: { day: 1 },
        max: 5,
      },
      cron: {
        enabled: true,
        schedule: "0 0 * * *",
      },
      licenseKey: env("HEADLOCKR_LICENSE_KEY"),
      admin: {
        auth: {
          shouldPersist: env("HEADLOCKR_ADMIN_AUTH_PERSIST", false),
          encryptionIv: env("ENCRYPTION_IV_HEADLOCKR"),
          encryptionKey: env("ENCRYPTION_KEY_HEADLOCKR"),
          backupEncryptionIv: env("ENCRYPTION_IV_HEADLOCKR_BACKUP"),
          backupEncryptionKey: env("ENCRYPTION_KEY_HEADLOCKR_BACKUP"),
          secret: env("MFA_SECRET"),
          phoneTokenSecret: env("PHONE_TOKEN_SECRET"),
          options: {
            expiresIn: "5m",
            audience: "strapi-admin-panel",
            issuer: "headlockr-admin-auth",
          },
          phoneOptions: {
            expiresIn: "5m",
            audience: "headlockr-phone-verification-service",
            issuer: "headlockr-phone-auth",
          },
        },
        trustedDevice: {
          enabled: env.bool("HEADLOCKR_TRUSTED_DEVICE_ENABLED", true),
          ttlMs: env.int("HEADLOCKR_TRUSTED_DEVICE_TTL_MS", 24 * 60 * 60 * 1000),
          cookieName: env("HEADLOCKR_TRUSTED_DEVICE_COOKIE_NAME", "headlockr_trusted_device"),
          secret: env("HEADLOCKR_TRUSTED_DEVICE_SECRET", env("MFA_SECRET")),
          requireRememberMe: env.bool("HEADLOCKR_TRUSTED_DEVICE_REQUIRE_REMEMBER_ME", false),
        },
      },
      contentApi: {
        auth: {
          encryptionIv: env("ENCRYPTION_IV_HEADLOCKR_CONTENT_API"),
          encryptionKey: env("ENCRYPTION_KEY_HEADLOCKR_CONTENT_API"),
          backupEncryptionIv: env("ENCRYPTION_IV_HEADLOCKR_CONTENT_API_BACKUP"),
          backupEncryptionKey: env("ENCRYPTION_KEY_HEADLOCKR_CONTENT_API_BACKUP"),
          secret: env("CONTENT_API_SECRET"),
          phoneTokenSecret: env("CONTENT_API_PHONE_TOKEN_SECRET"),
          options: {
            expiresIn: "5m",
            audience: "headlockr-content-api",
            issuer: "headlockr-content-api-auth",
          },
          phoneOptions: {
            expiresIn: "5m",
            audience: "headlockr-content-api-phone-verification-service",
            issuer: "headlockr-content-api-phone-auth",
          },
        },
      },
      passkeys: {
        rpId: env("HEADLOCKR_PASSKEY_RP_ID", "localhost"),
        rpName: env("HEADLOCKR_PASSKEY_RP_NAME", "Headlockr"),
        origins: env("HEADLOCKR_PASSKEY_ORIGINS", "http://localhost:1337,http://127.0.0.1:1337")
          .split(",")
          .map((origin) => origin.trim())
          .filter(Boolean),
        magicLogin: {
          enabled: env.bool("HEADLOCKR_PASSKEY_MAGIC_LOGIN_ENABLED", false),
        },
        webLogin: {
          enabled: env.bool("HEADLOCKR_PASSKEY_WEB_LOGIN_ENABLED", false),
        },
      },
      twilio: {
        accountSid: env("TWILIO_ACCOUNT_SID"),
        apiKeySid: env("TWILIO_API_KEY_SID"),
        apiKeySecret: env("TWILIO_API_KEY_SECRET"),
        from: env("TWILIO_PHONE_NUMBER"),
      },
      cookies: {
        httpOnly: false,
        secure: process.env.NODE_ENV === "production",
        sameSite: "Lax",
        path: "/",
      },
    },
  },
});`;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    alert("Copied to clipboard!");
  };

  return (
    <div className="px-lg-2 mt-1 rounded">
      <div className="alert bg-info text-white border-0 mb-3">
        Headlockr 5 no longer requires custom <code>vite.config.js</code> or Webpack changes.
      </div>

      <p className="text-light mb-3">
        Configure Headlockr inside <code>config/plugins.js</code> or <code>config/plugins.ts</code> just like any other Strapi plugin.
        This is where you enable passkeys, trusted devices, and the rest of your security setup.
      </p>

      <div className="position-relative mb-3">
        <ClientOnly
          fallback={
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          }
        >
          {() => <CodeBlock language="javascript">{configCode}</CodeBlock>}
        </ClientOnly>

        <div
          className="position-absolute top-0 end-0 m-2"
          onClick={() => copyToClipboard(configCode)}
        >
          <img
            src={copySuccess ? copyFull : copyEmpty}
            alt="Copy"
            style={{ width: "20px", cursor: "pointer" }}
          />
        </div>
      </div>

      <div className="bg-secondary rounded-4 p-3">
        <h5 className="mb-2">Upgrading from Headlockr 4.x?</h5>
        <p className="text-gray-neutral-800 mb-2">
          Remove the old custom Vite/Webpack Headlockr setup before installing v5. The legacy install flow is still documented for older projects.
        </p>
        <div className="d-flex flex-wrap gap-2">
          <a
            href="https://docs.headlockr.io/docs/getting-started/installation"
            target="_blank"
            rel="noreferrer noopener"
            className="btn btn-tertiary"
          >
            View Headlockr 5 docs
          </a>
          <a
            href="https://docs.headlockr.io/docs/4.x/getting-started/installation"
            target="_blank"
            rel="noreferrer noopener"
            className="btn btn-outline-light"
          >
            Headlockr 4.x legacy docs
          </a>
        </div>
      </div>
    </div>
  );
}
